import * as signalR from "@microsoft/signalr";
import { toast } from "../components/common/toast";
import { isNotificationVisible } from "../utils/notificationVisibility";
import {
  type Notification,
  useNotificationStore,
} from "../store/notification.store";
import {
  useReindexStore,
  type ReindexProgressEvent,
} from "../store/reindex.store";
import { useBulkImportStore } from "../store/bulkImport.store";
import {
  isBulkRunFinished,
  type BulkProgressEvent,
} from "../types/bulkImport.types";

/** Fired on window whenever the server says data changed: a contract's own
 *  update ("contract_updated") or a new business notification. Pages that
 *  auto-refresh (contract detail, Home, Dashboard) listen for it. */
export const DATA_CHANGED_EVENT = "lawsigna:data-changed";
export const CONTRACT_UPDATED_EVENT = "lawsigna:contract-updated";

export interface ContractUpdatedDetail {
  contractId: string;
  reason?: string;
  from?: string;
  subject?: string;
  statusChanged?: boolean;
}

class SignalRService {
  private connection: signalR.HubConnection | null = null;
  /** Contracts open on screen; re-joined after a reconnect, since the
   *  server forgets a connection's groups when it drops. */
  private watchedContracts = new Map<string, number>();

  /** Join a contract's live-update group (ref-counted per id). */
  watchContract(contractId: string) {
    if (!contractId) return;
    const n = this.watchedContracts.get(contractId) ?? 0;
    this.watchedContracts.set(contractId, n + 1);
    if (n === 0) void this.invokeSafe("WatchContract", contractId);
  }

  unwatchContract(contractId: string) {
    const n = this.watchedContracts.get(contractId) ?? 0;
    if (n <= 1) {
      this.watchedContracts.delete(contractId);
      void this.invokeSafe("UnwatchContract", contractId);
    } else {
      this.watchedContracts.set(contractId, n - 1);
    }
  }

  private async invokeSafe(method: string, ...args: unknown[]) {
    if (this.connection?.state !== signalR.HubConnectionState.Connected) {
      return; // re-sent from onreconnected / after start
    }
    try {
      await this.connection.invoke(method, ...args);
    } catch (error) {
      console.warn(`SignalR ${method} failed:`, error);
    }
  }

  private rejoinWatchedContracts() {
    this.watchedContracts.forEach((_n, id) => {
      void this.invokeSafe("WatchContract", id);
    });
  }

  async startConnection(token: string) {
    try {
      // Nothing to authenticate with yet (e.g. App.tsx mounts on /login,
      // before any token exists) — starting here would just hand
      // withAutomaticReconnect() an empty token to retry forever. The
      // caller re-invokes this once a token exists (see App.tsx).
      if (!token) {
        console.log("⚠️ SignalR: no access token yet, skipping connect.");
        return;
      }

      console.log("⚠️ SignalR Initializing...");

      if (this.connection) {
        console.log(
          "⚠️ Existing connection found, skipping duplicate builder.",
        );
        return;
      }

      // Same host as the REST API unless overridden: the gateway proxies
      // /notificationhub to the notification service. The old default of
      // port 6005 reached a stale host whose hub aborted every connection a
      // millisecond after the handshake, so live notifications never
      // arrived even though the REST side worked.
      const apiBase = (import.meta.env.VITE_API_BASE_URL || "").replace(
        /\/api\/v1\/?$/,
        "",
      );
      const baseUrl =
        import.meta.env.VITE_NOTIFICATION_API_BASE_URL ||
        apiBase ||
        window.location.origin;

      const hubUrl = `${baseUrl}/notificationhub`;
      console.log("✅ Target Pure WebSocket URL:", hubUrl);

      this.connection = new signalR.HubConnectionBuilder()
        .withUrl(hubUrl, {
          skipNegotiation: true,
          transport: signalR.HttpTransportType.WebSockets,
          // Read fresh from localStorage on every (re)connect attempt —
          // not just once at builder time. withAutomaticReconnect() reuses
          // this same connection object for every retry, so a token baked
          // statically into the URL string means every retry after the
          // original token expires keeps re-sending that same dead token
          // forever, which is what was producing the endless
          // disconnect/reconnect console noise.
          accessTokenFactory: () => localStorage.getItem("accessToken") || "",
        })
        .withAutomaticReconnect()
        .configureLogging(signalR.LogLevel.Information)
        .build();

      this.registerEvents();

      console.log("⚠️ Opening WebSocket connection stream...");
      await this.connection.start();

      console.log("✅ SignalR WebSocket Connected Successfully 🎉");
      this.rejoinWatchedContracts();
      console.log("Current Connection State:", this.connection.state);
    } catch (error) {
      console.error("❌ SIGNALR WEBSOCKET CONNECTION FAILED:", error);
    }
  }

  private registerEvents() {
    if (!this.connection) return;

    this.connection.on("business_notification", (data: any) => {
      console.log("📩 New Live Notification Received over WS Pipe:", data);
      const store = useNotificationStore.getState();

      const newNotification: Notification = {
        id: data.id,
        title: data.title,
        message: data.message || data.description,
        isRead: data.isRead !== undefined ? data.isRead : false,
        creationDate: data.creationDate || new Date().toISOString(),
        deepLinkUrl: data.deepLinkUrl || data.redirectUrl,
        notificationType: data.notificationType,
      };
      // Nothing about a module the isEmeis switch hides — no badge, no toast.
      if (!isNotificationVisible(newNotification)) return;

      store.addNotification(newNotification);
      // Most notifications follow a change to the data behind Home and the
      // Dashboard (an assignment, a workflow step, a reminder).
      window.dispatchEvent(new CustomEvent(DATA_CHANGED_EVENT));
      console.log("new notification", newNotification);
      toast.custom((t: any) => (
        <div
          key={t.id}
          className="
      min-w-[320px]
      rounded-2xl
      border border-[rgb(var(--border))]
      bg-[rgb(var(--card))]
      shadow-2xl
      p-4
      backdrop-blur-xl
    "
        >
          {/* <div className="flex items-start gap-3">
            <div className="mt-1 h-2 w-2 rounded-full bg-emerald-500" />
            <div className="flex-1">
              <div className="text-sm font-semibold text-[rgb(var(--text-primary))]">
                {newNotification.title}
              </div>
              {newNotification.message && (
                <div className="text-xs mt-1 text-[rgb(var(--text-secondary))]">
                  {newNotification.message}
                </div>
              )}
            </div>
          </div> */}
          <p className="text-sm font-medium text-[rgb(var(--text-primary))]">
            You have a new notification
          </p>
        </div>
      ));
    });

    this.connection.on("contract_updated", (payload: ContractUpdatedDetail) => {
      window.dispatchEvent(
        new CustomEvent<ContractUpdatedDetail>(CONTRACT_UPDATED_EVENT, {
          detail: payload,
        }),
      );
      window.dispatchEvent(new CustomEvent(DATA_CHANGED_EVENT));
    });

    this.connection.on("reindex_progress", (payload: ReindexProgressEvent) => {
      console.log("📩 Reindex progress received over WS Pipe:", payload);
      useReindexStore.getState().applyEvent(payload);
    });

    // Bulk upload progress: one message per sheet row, for whoever started
    // the upload. The run screen shows it; anywhere else, only its end is
    // worth interrupting for.
    this.connection.on("bulk_import_progress", (payload: BulkProgressEvent) => {
      useBulkImportStore.getState().applyEvent(payload);

      const ended = payload.rowNumber == null && isBulkRunFinished(payload.runStatus);
      const watching = window.location.pathname.startsWith("/contracts/bulk");
      if (ended && !watching && payload.message) toast.info(payload.message);
    });

    this.connection.onreconnecting(() => {
      console.warn("⚠️ SignalR Lost stream, reconnecting...");
    });

    this.connection.onreconnected(() => {
      console.log("✅ SignalR stream restored successfully.");
      this.rejoinWatchedContracts();
    });

    this.connection.onclose(() => {
      console.error("❌ SignalR Pipeline Terminated/Closed.");
    });
  }

  async stopConnection() {
    if (!this.connection) return;
    await this.connection.stop();
    this.connection = null;
    console.log("🛑 SignalR Pipe Explicitly Closed.");
  }
}

export default new SignalRService();
